import React, { useRef, useState } from 'react';
import { CloudArrowUp, Trash3 } from 'react-bootstrap-icons';
import { Spinner } from 'react-bootstrap';
import styles from './LomavisImageUpload1.module.css';

const DEFAULT_ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/png'];

export type LomavisImageUpload1Props = {
  onFileSelect: (file: File) => void | Promise<void>;
  disabled?: boolean;
  title?: string;
  description?: string;
  uploadingLabel?: string;
  uploadingDescription?: string;
  invalidFileMessage?: string;
  uploadErrorMessage?: string;
  /** MIME types accepted by the picker and validated after drop. */
  acceptedFileTypes?: string[];
  /** URL to show once the selected image has been uploaded. */
  imageSrc?: string;
  /** Clears the uploaded image and returns the control to its picker state. */
  onImageRemove?: () => void;
  removeLabel?: string;
  className?: string;
};

/**
 * A reusable single-image picker with click, keyboard, and drag-and-drop
 * support. It deliberately accepts only the PNG/JPEG formats supported by
 * Lomavis image uploads.
 */
export const LomavisImageUpload1: React.FC<LomavisImageUpload1Props> = ({
  onFileSelect,
  disabled = false,
  title = 'Select an image to upload',
  description = 'or drag and drop a PNG or JPG image here',
  uploadingLabel = 'Uploading image…',
  uploadingDescription = 'Please wait while your image is uploaded.',
  invalidFileMessage = 'Choose a PNG or JPG image.',
  uploadErrorMessage = 'Unable to upload this image. Please try again.',
  acceptedFileTypes = DEFAULT_ACCEPTED_FILE_TYPES,
  imageSrc,
  onImageRemove,
  removeLabel = 'Remove image',
  className = ''
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepthRef = useRef(0);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const isDisabled = disabled || isUploading;

  const selectFile = async (file?: File) => {
    if (!file || isDisabled) return;
    if (!acceptedFileTypes.includes(file.type)) {
      setError(invalidFileMessage);
      return;
    }
    setError(null);
    setIsUploading(true);
    try {
      await onFileSelect(file);
    } catch {
      setError(uploadErrorMessage);
    } finally {
      setIsUploading(false);
    }
  };
  const hasFiles = (event: React.DragEvent<HTMLElement>) =>
    Array.from(event.dataTransfer.types).includes('Files');

  return (
    <div className={className}>
      <input
        ref={inputRef}
        className="d-none"
        type="file"
        accept={acceptedFileTypes.join(',')}
        disabled={isDisabled}
        onChange={(event) => {
          void selectFile(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
      {imageSrc ? (
        <div className={styles.previewCard}>
          <div className={styles.previewWrapper}>
            <img
              className={styles.preview}
              src={imageSrc}
              alt="Uploaded image"
            />
            {isUploading && (
              <div className={styles.uploadingOverlay} role="status">
                <Spinner animation="border" size="sm" />
                <strong>{uploadingLabel}</strong>
              </div>
            )}
          </div>
          <button
            type="button"
            className={styles.removeButton}
            disabled={isDisabled || !onImageRemove}
            onClick={onImageRemove}
          >
            <Trash3 aria-hidden="true" size={14} />
            {removeLabel}
          </button>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={isDisabled ? -1 : 0}
          aria-label={title}
          aria-disabled={isDisabled}
          className={`${styles.dropzone} ${dragActive ? styles.dragActive : ''} ${
            isDisabled ? styles.disabled : ''
          }`}
          onClick={() => !isDisabled && inputRef.current?.click()}
          onKeyDown={(event) => {
            if (isDisabled || (event.key !== 'Enter' && event.key !== ' ')) return;
            event.preventDefault();
            inputRef.current?.click();
          }}
          onDragEnter={(event) => {
            if (!hasFiles(event)) return;
            event.preventDefault();
            if (isDisabled) return;
            dragDepthRef.current += 1;
            setDragActive(true);
          }}
          onDragOver={(event) => {
            if (!hasFiles(event)) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = isDisabled ? 'none' : 'copy';
          }}
          onDragLeave={(event) => {
            if (!hasFiles(event) || isDisabled) return;
            dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
            if (dragDepthRef.current === 0) setDragActive(false);
          }}
          onDrop={(event) => {
            if (!hasFiles(event)) return;
            event.preventDefault();
            dragDepthRef.current = 0;
            setDragActive(false);
            void selectFile(event.dataTransfer.files[0]);
          }}
        >
          {isUploading ? (
            <>
              <Spinner animation="border" size="sm" role="status" />
              <strong>{uploadingLabel}</strong>
              <small>{uploadingDescription}</small>
            </>
          ) : (
            <>
              <CloudArrowUp aria-hidden="true" size={30} />
              <strong>{title}</strong>
              <small>{description}</small>
            </>
          )}
        </div>
      )}
      {error && <div className="mt-1 small text-danger">{error}</div>}
    </div>
  );
};
